import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined'
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined'
import {
    setGraphicsQuality,
    setShadowsEnabled,
    setSunAngle,
    setSunIntensity,
    setSunVertical,
} from '../../actions/sceneActions'

const clamp = (value, min, max, fallback) => {
    const number = Number(value)
    if (!Number.isFinite(number)) return fallback
    return Math.min(max, Math.max(min, number))
}

const SceneGuestViewControls = ({
    open,
    onClose,
    authorSettings = {},
}) => {
    const dispatch = useDispatch()
    const scene = useSelector((state) => state.scene || {})

    const values = useMemo(() => ({
        sunAngle: clamp(scene.sunAngle, 0, 360, 315),
        sunVertical: clamp(scene.sunVertical, 0, 90, 45),
        sunIntensity: clamp(scene.sunIntensity, 0.1, 3, 1.5),
        graphicsQuality: ['low', 'medium', 'high'].includes(scene.graphicsQuality)
            ? scene.graphicsQuality
            : 'medium',
        shadowsEnabled: scene.shadowsEnabled !== false,
    }), [scene])

    if (!open) return null

    const applyQuality = (quality) => {
        dispatch(setGraphicsQuality(quality))

        if (quality === 'low') {
            dispatch(setShadowsEnabled(false))
        } else {
            dispatch(setShadowsEnabled(true))
        }
    }

    const resetAuthorView = () => {
        dispatch(setSunAngle(clamp(authorSettings.sunAngle, 0, 360, 315)))
        dispatch(setSunVertical(clamp(authorSettings.sunVertical, 0, 90, 45)))
        dispatch(setSunIntensity(clamp(authorSettings.sunIntensity, 0.1, 3, 1.5)))

        const quality = ['low', 'medium', 'high'].includes(authorSettings.graphicsQuality)
            ? authorSettings.graphicsQuality
            : 'medium'

        dispatch(setGraphicsQuality(quality))
        dispatch(setShadowsEnabled(authorSettings.shadowsEnabled !== false && quality !== 'low'))
    }

    return (
        <aside className="sceneGuestViewControls" aria-label="Настройки вида">
            <button
                type="button"
                className="sceneGuestViewControls__backdrop"
                onClick={onClose}
                aria-label="Закрыть настройки вида"
            />

            <section className="sceneGuestViewControls__panel">
                <header>
                    <div>
                        <span>Отображение</span>
                        <strong>Свет и качество</strong>
                    </div>

                    <button type="button" onClick={onClose} aria-label="Закрыть">
                        <CloseOutlinedIcon />
                    </button>
                </header>

                <div className="sceneGuestViewControls__quality">
                    <span>Качество</span>
                    <div>
                        {[
                            ['low', 'Низкое'],
                            ['medium', 'Среднее'],
                            ['high', 'Высокое'],
                        ].map(([value, label]) => (
                            <button
                                key={value}
                                type="button"
                                className={values.graphicsQuality === value ? 'is-active' : ''}
                                onClick={() => applyQuality(value)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <small>
                        На слабом устройстве выбирайте низкое качество — тени отключатся автоматически.
                    </small>
                </div>

                <div className="sceneGuestViewControls__sliders">
                    <label>
                        <span>
                            <WbSunnyOutlinedIcon />
                            Положение солнца
                            <b>{Math.round(values.sunAngle)}°</b>
                        </span>
                        <input
                            type="range"
                            min="0"
                            max="360"
                            step="1"
                            value={values.sunAngle}
                            onChange={(event) => dispatch(setSunAngle(Number(event.target.value)))}
                        />
                    </label>

                    <label>
                        <span>
                            Высота солнца
                            <b>{Math.round(values.sunVertical)}°</b>
                        </span>
                        <input
                            type="range"
                            min="0"
                            max="90"
                            step="1"
                            value={values.sunVertical}
                            onChange={(event) => dispatch(setSunVertical(Number(event.target.value)))}
                        />
                    </label>

                    <label>
                        <span>
                            Яркость
                            <b>{values.sunIntensity.toFixed(1)}</b>
                        </span>
                        <input
                            type="range"
                            min="0.1"
                            max="3"
                            step="0.1"
                            value={values.sunIntensity}
                            onChange={(event) => dispatch(setSunIntensity(Number(event.target.value)))}
                        />
                    </label>
                </div>

                <button
                    type="button"
                    className="sceneGuestViewControls__reset"
                    onClick={resetAuthorView}
                >
                    <RestartAltOutlinedIcon />
                    Вернуть вид автора
                </button>
            </section>
        </aside>
    )
}

export default SceneGuestViewControls
